import type { CarrierTrackingSummary } from "./carrierTracking";

export interface ShipmentPlace {
  lat: number | null;
  long: number | null;
  address: string | null;
}

export interface ShipmentMerchandise {
  id?: string;
  description: string;
  quantity: number;
  unitType: string;
  weight?: number;
  volume?: number;
}

const finiteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const normalizeMerchandiseItem = (value: unknown): ShipmentMerchandise => {
  const raw = (value ?? {}) as Record<string, unknown>;
  const weight = finiteNumber(raw.weight);
  const volume = finiteNumber(raw.volume);
  return {
    ...(typeof raw.id === "string" && raw.id ? { id: raw.id } : {}),
    description: typeof raw.description === "string" ? raw.description : "",
    quantity: finiteNumber(raw.quantity) ?? 1,
    unitType: typeof raw.unitType === "string" ? raw.unitType : "",
    ...(weight !== undefined && { weight }),
    ...(volume !== undefined && { volume }),
  };
};

export const normalizeMerchandise = (value: unknown): ShipmentMerchandise[] => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item && typeof item === "object")
      .map(normalizeMerchandiseItem);
  }
  return value && typeof value === "object" ? [normalizeMerchandiseItem(value)] : [];
};

export const SHIPMENT_EVENT_TYPES = [
  "pickup",
  "transit",
  "intermediate",
  "warehouse",
  "customs",
  "last_mile",
  "delivered",
  "incident",
] as const;

export type ShipmentEventType = (typeof SHIPMENT_EVENT_TYPES)[number];

export const isShipmentEventType = (
  value: string | null | undefined,
): value is ShipmentEventType =>
  !!value && (SHIPMENT_EVENT_TYPES as readonly string[]).includes(value);

export const SHIPMENT_TRIP_UNIT_STATES = ["live", "closed_only", "none"] as const;
export type ShipmentTripUnitState = (typeof SHIPMENT_TRIP_UNIT_STATES)[number];

export const isShipmentTripUnitState = (value: unknown): value is ShipmentTripUnitState =>
  (SHIPMENT_TRIP_UNIT_STATES as readonly unknown[]).includes(value);

export const isShipmentUnseenByDriver = (shipment: {
  trip_id: string | null;
  trip_unit_state: ShipmentTripUnitState | null;
}): boolean => !!shipment.trip_id && !!shipment.trip_unit_state && shipment.trip_unit_state !== "live";

export const SHIPMENT_TRACKING_MODES = ["manual", "by_unit", "by_trip"] as const;

export type ShipmentTrackingMode = (typeof SHIPMENT_TRACKING_MODES)[number];

export const isShipmentTrackingMode = (
  value: string | null | undefined,
): value is ShipmentTrackingMode =>
  !!value && (SHIPMENT_TRACKING_MODES as readonly string[]).includes(value);

export interface ShipmentEvent {
  id?: string;
  device_id?: string;
  event_type: string;
  lat: number | null;
  long: number | null;
  address: string | null;
  note: string | null;
  date: string;
  file_url?: string;
  file_urls?: string[];
  /** Origen del evento: "driver_app", "carrier" (paquetería 17TRACK), etc. */
  source?: string;
  /** Llave de idempotencia; la paquetería deduplica con ella. No se pierde al reescribir el historial. */
  client_event_id?: string;
  /** Estatus normalizado de la paquetería que generó el evento (source = "carrier"). */
  carrier_status?: string;
}

export interface ShipmentContact {
  id: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  company_id?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface Shipment {
  id: string;
  client_info?: {
    client_name: string;
    client_email: string;
    client_phone: string;
  };
  client_id?: string;
  client_name: string;
  shipment_name: string;
  company_id: string | null;
  actual_device_id?: string | null;
  origin: ShipmentPlace;
  destination: ShipmentPlace;
  tracking_number: string;
  transportation_type: string;
  /** Expediente documental (CC-CONSOLE #1632): `importacion|exportacion|nacional`; sin valor cuenta como nacional. */
  direction?: string | null;
  incoterm?: string | null;
  status_id: string;
  note: string | null;
  event_history: ShipmentEvent[];
  contacts?: ShipmentContact[] | null;
  notification_frequency?: string | null;
  merchandise?: ShipmentMerchandise[];
  tracking_link_id: string | null;
  trip_id: string | null;
  trip_unit_state: ShipmentTripUnitState | null;
  smart_tracking: ShipmentTrackingMode;
  /** Resumen de la guía de paquetería (17TRACK), si el envío tiene una. */
  carrier_tracking?: CarrierTrackingSummary | null;
  created_at?: unknown;
  updated_at?: unknown;
}

export const isShipmentEligibleForTrip = (shipment: Shipment): boolean =>
  !shipment.trip_id;

export interface ShipmentEventInput {
  event_type: ShipmentEventType;
  lat: number | null;
  long: number | null;
  address: string | null;
  note: string | null;
}

export interface UpdateShipmentData {
  actual_device_id?: string | null;
  client_name?: string;
  shipment_name?: string;
  origin?: ShipmentPlace;
  destination?: ShipmentPlace;
  transportation_type?: string;
  direction?: string | null;
  incoterm?: string | null;
  status_id?: string;
  note?: string | null;
  event_history?: ShipmentEvent[];
  tracking_link_id?: string | null;
  trip_id?: string | null;
  merchandise?: ShipmentMerchandise[];
}

export const buildShipmentEvent = (input: ShipmentEventInput): ShipmentEvent => ({
  id: `ev-${Date.now()}`,
  event_type: input.event_type,
  lat: input.lat,
  long: input.long,
  address: input.address,
  note: input.note,
  date: new Date().toISOString(),
});

export const appendShipmentEvent = (
  shipment: Shipment,
  input: ShipmentEventInput,
): ShipmentEvent[] => [...shipment.event_history, buildShipmentEvent(input)];

export const lastShipmentEvent = (shipment: Shipment): ShipmentEvent | null =>
  shipment.event_history.length > 0
    ? shipment.event_history[shipment.event_history.length - 1]
    : null;

export const shipmentEventTypeOf = (
  shipment: Shipment,
): ShipmentEventType | null => {
  const type = lastShipmentEvent(shipment)?.event_type;
  return isShipmentEventType(type) ? type : null;
};

export const SHIPMENT_STAGES = [
  "pending",
  "transit",
  "delivered",
  "incident",
] as const;

export type ShipmentStage = (typeof SHIPMENT_STAGES)[number];

const STAGE_BY_EVENT_TYPE = {
  pickup: "transit",
  transit: "transit",
  intermediate: "transit",
  warehouse: "transit",
  customs: "transit",
  last_mile: "transit",
  delivered: "delivered",
  incident: "incident",
} as const satisfies Record<ShipmentEventType, ShipmentStage>;

/**
 * Etapa a la que lleva un tipo de evento.
 *
 * Existe para la analitica: `to_stage` tiene que hablar SIEMPRE el vocabulario
 * de etapas (4 valores). El formulario de detalle trabaja con tipos de evento
 * (8 valores) y, si se mandaban tal cual, la misma transicion de negocio
 * quedaba partida en dos vocabularios — el kanban reportando "transit" y el
 * detalle "pickup"/"customs"/"warehouse" como si fueran etapas.
 */
export const stageForEventType = (
  eventType: ShipmentEventType,
): ShipmentStage => STAGE_BY_EVENT_TYPE[eventType];

export const STAGE_EVENT_TYPE = {
  transit: "transit",
  delivered: "delivered",
  incident: "incident",
} as const satisfies Partial<Record<ShipmentStage, ShipmentEventType>>;

export const isDroppableStage = (
  stage: ShipmentStage,
): stage is keyof typeof STAGE_EVENT_TYPE => stage in STAGE_EVENT_TYPE;

export const shipmentPosition = (
  shipment: Shipment,
): { lat: number; long: number } | null => {
  const event = [...shipment.event_history]
    .reverse()
    .find((item) => item.lat !== null && item.long !== null);
  const place = event ?? shipment.origin;
  return place.lat !== null && place.long !== null
    ? { lat: place.lat, long: place.long }
    : null;
};

export const SHIPMENT_PROGRESSION = [
  "pickup",
  "transit",
  "warehouse",
  "customs",
  "last_mile",
  "delivered",
] as const;

export type ShipmentProgressionType = (typeof SHIPMENT_PROGRESSION)[number];

export const isProgressionType = (
  value: string | null | undefined,
): value is ShipmentProgressionType =>
  !!value && (SHIPMENT_PROGRESSION as readonly string[]).includes(value);

const progressionIndex = (type: ShipmentProgressionType): number =>
  SHIPMENT_PROGRESSION.indexOf(type);

export const furthestProgressionOf = (
  eventTypes: readonly string[],
): ShipmentProgressionType | null =>
  eventTypes.reduce<ShipmentProgressionType | null>(
    (furthest, type) =>
      isProgressionType(type) &&
      (furthest === null || progressionIndex(type) > progressionIndex(furthest))
        ? type
        : furthest,
    null,
  );

export const furthestProgressionType = (
  shipment: Shipment,
): ShipmentProgressionType | null =>
  furthestProgressionOf(shipment.event_history.map((event) => event.event_type));

export const isBackwardsEvent = (
  shipment: Shipment,
  eventType: ShipmentEventType,
): boolean => {
  const furthest = furthestProgressionType(shipment);
  return (
    isProgressionType(eventType) &&
    furthest !== null &&
    progressionIndex(eventType) < progressionIndex(furthest)
  );
};

export const shipmentStage = (shipment: Shipment): ShipmentStage => {
  const last = shipmentEventTypeOf(shipment);
  if (last === "incident") return "incident";

  const furthest = furthestProgressionType(shipment);
  if (furthest) return STAGE_BY_EVENT_TYPE[furthest];

  return last ? STAGE_BY_EVENT_TYPE[last] : "pending";
};
